import React from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlass } from '@phosphor-icons/react';

import { Icon } from '../../design-system/components/Icon';
import { cn } from '../../design-system/lib/cn';
import { useViewport } from '../../design-system/hooks/useViewport';
import { Skeleton } from '../../design-system/components/desktop';
import { SearchField } from '../../design-system/components/mobile';

import './sets.css';

/**
 * The tile index, archetype A10. Shared by `/sets` and `/sets/category/:tcgId`.
 *
 * WHY THIS IS NOT `ListingCard`
 * -----------------------------
 * `patterns/market` owns THE listing card and nothing else may build one. A
 * tile here is not a listing: it has no price, no rarity, no condition, no
 * availability and no card number, which is the whole of `ListingCard`'s fixed
 * anatomy. Its payload is a logo, a name, and one optional line. Forcing it
 * through the listing card would mean either shipping an empty price row or
 * inventing money that this endpoint does not return, and the upstream
 * templates agree: `sets.html` and `set-cards.html` both build these as a plain
 * card surface, not as `.dsk-listing-card`. Anything on these pages that IS a
 * listing still goes through `patterns/market`.
 *
 * WHY THERE IS NO `useViewport()` BRANCH ON THE TILE
 * --------------------------------------------------
 * The tile DOM is identical on both tracks and only its metrics change, which
 * `design-system/README.md` section 5 assigns to a CSS media query. The hook is
 * used exactly twice below, for the search field and the empty-state sizing,
 * where the two tracks genuinely render different elements.
 */

/* ------------------------------------------------------------------ header */

/**
 * The page header. Desktop gets the 28px `.dsk-page-title`, mobile the 24px
 * `.ds-screen-title`, because that is a type-scale difference between two
 * tracks and each track owns its own scale.
 */
export function TileIndexHeader({ title, sub }) {
  const { isDesktop } = useViewport();

  return (
    <header className="dsa-tileindex__head">
      {isDesktop ? (
        <h1 className="dsk-page-title">{title}</h1>
      ) : (
        <h1 className="ds-screen-title">{title}</h1>
      )}
      {sub ? <p className="dsa-tileindex__sub">{sub}</p> : null}
    </header>
  );
}

/* ---------------------------------------------------------------- controls */

/**
 * The in-page filter field.
 *
 * Genuinely different markup per track (`.dsk-search` is a bare flex row with a
 * Phosphor glyph, `.ds-search` is a 44px label-wrapped pill with its own inline
 * SVG), so this is one of the cases the hook exists for. Neither carries a
 * keyboard-shortcut chip: that belongs to the nav search alone.
 */
export function TileIndexSearch({ value, onChange, placeholder, label }) {
  const { isDesktop } = useViewport();

  if (!isDesktop) {
    return (
      <div className="dsa-tileindex__controls">
        <SearchField
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          aria-label={label}
        />
      </div>
    );
  }

  return (
    <div className="dsa-tileindex__controls">
      <label className="dsk-search">
        <Icon as={MagnifyingGlass} />
        <input
          type="search"
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          aria-label={label}
          onChange={(event) => onChange(event.target.value)}
        />
      </label>
    </div>
  );
}

/* ------------------------------------------------------------------- grid */

export function TileGrid({ dense = false, className, ...rest }) {
  return <div className={cn('dsa-tile-grid', dense && 'dsa-tile-grid--dense', className)} {...rest} />;
}

/**
 * The monogram a logo plate falls back to.
 *
 * The games index used `name.split(' ')[0]` — the first WORD — so every tile
 * printed its own name twice: "Magic:" above "Magic: The Gathering", "Pokemon"
 * above "Pokemon". Two of the seven games were the same word repeated and two
 * carried a trailing colon. Not an edge case either: no game in the catalogue
 * has a logo, so the plate is what every buyer sees.
 *
 * Initials, because for games the abbreviation is one people actually use —
 * MTG, OPC, DBS. Words under three letters are skipped so "League of Legends"
 * gives RLL and not RLO; a single-word name has no initials to take, so it
 * falls back to two letters, which is what the sets index does for every set.
 * Capped at three: this is a plate, not a label.
 */
export function monogram(name) {
  const words = String(name || '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();

  const significant = words.filter((word) => word.length >= 3);
  const source = significant.length >= 2 ? significant : words;
  return source
    .slice(0, 3)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
}

/* ------------------------------------------------------------------- tile */

/**
 * One tile.
 *
 * `to` makes it a real `<Link>`, which is what the app should always have used:
 * the legacy markup was a `<div onClick>`, so the whole index was unreachable
 * by keyboard and could not be opened in a new tab. `onClick` is still honoured
 * for the case where the destination is computed rather than routed.
 *
 * `image` falls back to a text plate, exactly as the app does, and the plate is
 * the caller's string because the two pages truncate differently: the games
 * index uses the first word, the sets index the first two letters uppercased.
 */
export function Tile({ to, state, onClick, image, imageAlt = '', placeholder, name, meta, description }) {
  const Root = to ? Link : 'button';
  /* `state` rides along with `to`, and it has to be destructured to get there:
     this component takes no `...rest`, so a prop it does not name is silently
     dropped. That is how the set index's hand-off to the filtered marketplace
     went missing — the state was passed in and never reached the Link. */
  const rootProps = to ? { to, state } : { type: 'button' };

  return (
    <Root className="dsa-tile" onClick={onClick} {...rootProps}>
      <span
        className={cn('dsa-tile__logo', !image && 'dsa-tile__logo--placeholder')}
        aria-hidden={image ? undefined : 'true'}
      >
        {image ? (
          <img src={image} alt={imageAlt} />
        ) : (
          <span className="dsa-tile__initials">{placeholder}</span>
        )}
      </span>

      <h3 className="dsa-tile__name">{name}</h3>
      {meta ? <p className="dsa-tile__count">{meta}</p> : null}
      {description ? <p className="dsa-tile__desc">{description}</p> : null}
    </Root>
  );
}

/* --------------------------------------------------------------- skeleton */

/**
 * The loading grid.
 *
 * Same grid, same tile chrome, same logo height, so nothing reflows when the
 * data lands. The bars are the shared `Skeleton`, which replaces the two
 * hand-rolled skeleton components this directory used to carry.
 *
 * Skeletons are decorative and every bar is already `aria-hidden`, so the
 * announcement lives on the region: the caller marks it `aria-busy`.
 */
export function TileGridSkeleton({ count = 8, dense = false, lines = 2 }) {
  const tiles = [];
  for (let i = 0; i < count; i += 1) tiles.push(i);

  return (
    <TileGrid dense={dense} aria-hidden="true">
      {tiles.map((i) => (
        <div key={i} className="dsa-tile dsa-tile--skeleton">
          <span className="dsa-tile__logo">
            <Skeleton shape="none" className="dsa-tile__skel-logo" />
          </span>
          <Skeleton shape="title" width="62%" className="dsa-tile__skel-name" />
          {lines > 1 ? (
            <Skeleton shape="text" width="82%" className="dsa-tile__skel-line" />
          ) : (
            <Skeleton shape="text" width="42%" className="dsa-tile__skel-line" />
          )}
        </div>
      ))}
    </TileGrid>
  );
}
